import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// API Response Types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
  success: boolean;
}

export interface MetaResponse<T = any> {
  data: T;
  meta: {
    code: number;
    message: string | null;
    error: string | null;
  }
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// Error Types
export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

// Request Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginThesisRequest {
  email: string;
  password: string;
  g_recaptcha_response?: string;
}

export interface SearchAlumniRequest {
  nib_nik: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface SubmitProfileAlumniRequest {
  nib_nik: string;
  nama_lengkap: string | null;
  gelar_akademik_depan: string;
  gelar_akademik_belakang: string;
  foto_profil: File | null;
  linkedin_profile: string;
  program_beasiswa: string | null;
  universitas: string | null;
  negara: string | null;
  tahun_lulus: number | null;
  bidang_studi: string | null;
  pekerjaan_saat_ini: string | null;
  perusahaan_instansi: string | null;
  prestasi_utama: string | null;
  biography: string | null;
  kontribusi_masyarakat: string | null;
  bidang_sektor: string | null;
  deskripsi_singkat_pekerjaan: string | null;
  kutipan_inspiratif: string | null;
  pesan_untuk_calon_awardee: string | null;
  kenangan_paling_berkesan_studi: string | null;
  is_setuju: number;
  submit_by_admin: boolean;
}

export interface BidangSektor {
  value: string;
  label: string;
}

// Response Types
export interface User {
  users_id: number;
  users_firstname: string;
  users_lastname: string;
  email: string;
  users_status: string;
  token: string;
  path_url_foto: string;
  tipe_peneliti_tesis: string;
  instansi_tesis: string;
  institusi_tesis: string;
  perusahaan_tesis: string;
  departemen_tesis: string;
  divisi_tesis: string;
  jabatan_tesis: string;
  fullname: string;
  detail: {
    user_details_id: number;
    user_details_users_id: string;
    user_details_id_card_number: string | null;
    NIK: string | null;
    document_url_full: string | null;
  };
}

export interface UserAlumni {
  users_id: number;
  users_firstname: string;
  users_lastname: string;
  users_username: string;
  email: string;
  users_created_by: number | null;
  users_updated_by: number | null;
  users_status: number;
  remember_token: string | null;
  created_at: string;
  updated_at: string;
  is_login_api: number;
  is_pembaruan_profile: number;
	is_pana: number | null,
	is_pakta_integritas: number | null,
	users_kode: string | null,
	token: string,
	role_name: string,
	fullname: string
}

export interface Thesis {
  id: number;
  title: string;
  abstract: string;
  author: string;
  scientific_field: string;
  language: string;
  publish_date: string;
  country: string;
  file_url?: string;
}

export interface ScientificField {
  id: number;
  name: string;
  code: string;
}

export interface Language {
  id: number;
  name: string;
  code: string;
}

export interface ThesisFeedback {
  id: number;
  thesis_id: number;
  user_id: number;
  feedback: string;
  rating: number;
  created_at: string;
}

export interface Profile {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  address?: string;
  institution?: string;
  position?: string;
}

export interface AlumniProfile {
  users_id: number;
	users_firstname: string;
	users_lastname: string;
	users_username: string;
	email: string;
	users_created_by: number | null;
	users_updated_by: number | null;
	users_status: number;
	remember_token: string | null;
	created_at: string;
	updated_at: string;
	is_login_api: number;
	is_pembaruan_profile: number;
	is_pana: number | null;
	is_pakta_integritas: number | null;
	users_kode: string | null;
	id_hash: string;
	fullname: string;
  detail: AlumniProfileDetail;
  addresses: AlumniProfileAddress[];
  experiences: AlumniProfileExperience[];
  educations: AlumniProfileEducation[];
  skills: any;
  languages: any;
  others: any;
}

export interface AlumniProfileAddress {
  user_addresses_id: number;
  user_addresses_users_id: number;
  user_addresses_provinces_id: number | null;
  user_addresses_cities_id: number | null;
  user_addresses_districts_id: number | null;
  user_addresses_villages_id: number | null;
  user_addresses_postal_code: string | null;
  user_addresses_is_domicile: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user_addresses: string;
  province: string | null;
  city: string | null;
  district: string | null;
  village: string | null;
}

export interface AlumniProfileExperience {
  user_experiences_id: number;
  user_experiences_users_id: number;
  user_experiences_title: string;
  user_experiences_company_name: string | null;
  user_experiences_start_date: string | null;
  user_experiences_end_date: string | null;
  user_experiences_total: number | null;
  user_experiences_industries_id: number | null;
  user_experiences_description: string | null;
  user_experiences_document: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user_experiences_working_types_id: number | null;
  document_url_full: string | null;
  industry: string | null;
  working_type: string | null;
  company: string | null;
}

export interface AlumniProfileEducation {
  user_educations_id: number;
  user_educations_users_id: number;
  user_educations_universities_id: number;
  user_educations_degrees_id: number;
  user_educations_start_date: string;
  user_educations_end_date: string;
  user_educations_total: string;
  user_educations_gpa: string | null;
  user_educations_description: string | null;
  user_educations_certificate: string | null;
  user_educations_transcript: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user_educations_majors_id: number;
  user_educations_gpa_max: string | null;
  certificate_url_full: string | null;
  transcript_url_full: string | null;
  university: {
    universities_id: number;
    universities_name: string;
    universities_description: string;
    universities_is_active: number;
    created_at: string;
    updated_at: string;
    auto_integration: number;
    deleted_at: string | null;
  };
  degree: {
    degrees_id: number;
    degrees_name: string;
    degrees_description: string;
    degrees_is_active: number;
    created_at: string;
    updated_at: string;
    auto_integration: number;
    deleted_at: string | null;
  };
  major: {
    majors_id: number;
    majors_name: string;
    majors_description: string;
    majors_is_active: number;
    created_at: string;
    updated_at: string;
    auto_integration: number;
    deleted_at: string | null;
  };
}

export interface AlumniProfileDetail {
  user_details_id: number;
  user_details_users_id: number;
  user_details_id_card_number: string;
  user_details_family_card_number: string | null;
  user_details_tax_id_number: string | null;
  user_details_code_phone: number;
  user_details_phone: string;
  user_details_wa_code_phone: string | null;
  user_details_wa_phone: string | null;
  user_details_fb: string | null;
  user_details_ig: string | null;
  user_details_twitter: string | null;
  user_details_youtube: string | null;
  user_details_gender: number;
  user_details_photo: string;
  user_details_document: string | null;
  user_details_description: string | null;
  created_at: string;
  updated_at: string;
  dob: string;
  alternative_email: string;
  registration_number: string;
  registration_code: string;
  sk_selection_number: string;
  father_name: string;
  mother_name: string;
  company: string | null;
  company_site: string | null;
  is_phone: number | null;
  is_email: number | null;
  company_phone: string | null;
  tipe_peneliti_tesis: string | null;
  instansi_tesis: string | null;
  institusi_tesis: string | null;
  perusahaan_tesis: string | null;
  departemen_tesis: string | null;
  divisi_tesis: string | null;
  jabatan_tesis: string | null;
  is_kerja: number;
  document_url_full: string | null;
  no_rekening?: string | null;
  nama_rekening?: string | null;
}

export interface SearchAlumniResponse {
  nama_lengkap: string;
  nik: string;
  batch_pk?: string | null;
  jenjang_studi?: string | null;
  negara?: string | null;
  prodi?: string | null;
  status_beasiswa?: string | null;
  program_beasiswa?: string | null;
  tahun_lulus?: string | null;
  universitas?: string | null;
  role: string;
}
// Query Parameters
export interface ThesisQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  scientific_field_id?: number;
  language_id?: number;
  country?: string;
  publish_date_from?: string;
  publish_date_to?: string;
}

export interface ScientificFieldQueryParams {
  search?: string;
  page?: number;
  per_page?: number;
}

export interface AlumniSubmission {
  alumni_profile_id: string;
  nama_alumni: string;
  gelar_akademik: string;
  program: string;
  universitas: string;
  tanggal_pengajuan: string;
  status: string;
  foto_profil_url: string;
  submitted_at: string;
  updated_at: string;
}

export interface AlumniHistoryPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface AlumniHistoryResponse {
  submissions: AlumniSubmission[];
  pagination: AlumniHistoryPagination;
  total_submissions: number;
}

// API Configuration
export interface ApiConfig {
  baseURL: string;
  timeout: number;
  headers?: Record<string, string>;
}

// Master Table Types
export interface MasterTableItem {
  id: number;
  title_id: string;
  deskripsi_id: string;
  image: string;
  is_active: number;
  tipe: string;
  created_at: string;
  updated_at: string;
}

export interface MasterTableQueryParams {
  tipe?: string;
  page?: number;
  per_page?: number;
  search?: string;
}

class ApiErrorResponse extends Error {
  constructor(
    public status: number,
    public message: string,
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiErrorResponse';
  }
}

export class LandingPageApi {
  private client: AxiosInstance;
  private apiToken: string | null = null;

  constructor(config?: Partial<ApiConfig>) {
    const defaultConfig: ApiConfig = {
      baseURL: process.env.REACT_APP_BASE_API_URL || '',
      timeout: 31000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    };

    const finalConfig = { ...defaultConfig, ...config };
    
    this.client = axios.create(finalConfig);
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Get token from localStorage if not set in instance
        const token = localStorage.getItem('apiToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response) {
          const { status, data } = error.response;
          // Handle the meta response structure
          const message = data.meta?.message || data.message || 'An error occurred';
          const errors = data.meta?.error ? { general: [data.meta.error] } : data.errors;
          throw new ApiErrorResponse(
            status,
            message,
            errors
          );
        }
        throw new ApiErrorResponse(0, error.message || 'Network error');
      }
    );
  }

  // Token management
  public setToken(token: string): void {
    this.apiToken = token;
    localStorage.setItem('apiToken', token);
  }

  public setUmum(umum: string): void {
    localStorage.setItem('is_umum', umum.toString());
  }

  public setUser(user: User | UserAlumni): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  public setThesisResearcherType(type: string): void {
    localStorage.setItem('thesisResearcherType', type);
  }

  public getToken(): string | null {
    if (!this.apiToken) {
      this.apiToken = localStorage.getItem('apiToken');
    }
    return this.apiToken;
  }

  public clearToken(): void {
    this.apiToken = null;
    localStorage.removeItem('apiToken');
  }

  // Generic request methods
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error) {
      if (error instanceof ApiErrorResponse) {
        throw error;
      }
      throw new ApiErrorResponse(0, 'Unknown error occurred');
    }
  }

  private async get<T>(url: string, params?: any): Promise<T> {
    return this.request<T>({ method: 'GET', url, params });
  }

  private async post<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'POST', url, data });
  }

  private async put<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data });
  }

  private async delete<T>(url: string): Promise<T> {
    return this.request<T>({ method: 'DELETE', url });
  }

  // Authentication methods
  public async loginThesis(data: LoginThesisRequest): Promise<MetaResponse<User>> {
    const response = await this.post<MetaResponse<User>>('/auth/login-tesis', data);
    if (response.meta.code === 200 && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data);
      this.setThesisResearcherType(response.data.tipe_peneliti_tesis);
      this.setUmum('1')
    }
    return response;
  }

  public async loginAlumni(data: LoginRequest): Promise<MetaResponse<UserAlumni>> {
    const response = await this.post<MetaResponse<UserAlumni>>('/auth/login', data);
    if (response.meta.code === 200 && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data);
      this.setUmum('0')
    }
    return response;
  }

  public async loginBackdoor(data: LoginRequest): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.post<ApiResponse<{ user: User; token: string }>>('/auth/admin-login-as-alumni', data);
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
    }
    return response;
  }

  public async register(data: RegisterRequest): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.post<ApiResponse<{ user: User; token: string }>>('/auth/register-tesis', data);
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
    }
    return response;
  }

  public async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/change-password', data);
  }

  public async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/auth/forgot-password', data);
  }

  // Thesis methods
  public async getThesis(params?: ThesisQueryParams): Promise<PaginatedResponse<Thesis>> {
    return this.get<PaginatedResponse<Thesis>>('/list/tesis', params);
  }

  public async getThesisAdmin(params?: ThesisQueryParams): Promise<PaginatedResponse<Thesis>> {
    return this.get<PaginatedResponse<Thesis>>('/list/tesis/admin', params);
  }

  public async getThesisDetail(id: number): Promise<ApiResponse<Thesis>> {
    return this.get<ApiResponse<Thesis>>(`/detail/tesis/${id}`);
  }

  public async getTotalThesis(): Promise<ApiResponse<{ total: number }>> {
    return this.get<ApiResponse<{ total: number }>>('/list/total-tesis');
  }

  public async getTotalThesisPublic(): Promise<ApiResponse<{ total: number }>> {
    return this.get<ApiResponse<{ total: number }>>('/list/total-tesis-publik');
  }

  public async getThesisFile(id: number): Promise<Blob> {
    const response = await this.client.get(`/detail/tesis/enkrip/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Scientific fields and languages
  public async getScientificField(params?: ScientificFieldQueryParams): Promise<PaginatedResponse<ScientificField>> {
    return this.get<PaginatedResponse<ScientificField>>('/list/bidang-keilmuan', params);
  }

  public async getLanguage(params?: ScientificFieldQueryParams): Promise<PaginatedResponse<Language>> {
    return this.get<PaginatedResponse<Language>>('/list/bahasa', params);
  }

  // Feedback methods
  public async getThesisFeedback(thesisId: number): Promise<ApiResponse<ThesisFeedback[]>> {
    return this.get<ApiResponse<ThesisFeedback[]>>(`/list/feedback/${thesisId}`);
  }

  public async storeThesisFeedback(data: { thesis_id: number; feedback: string; rating: number }): Promise<ApiResponse<ThesisFeedback>> {
    return this.post<ApiResponse<ThesisFeedback>>('/tesis/feedback', data);
  }

  // Profile methods
  public async getProfileTesis(): Promise<ApiResponse<Profile>> {
    return this.get<ApiResponse<Profile>>('/profile');
  }

  public async getProfileAlumni(): Promise<ApiResponse<AlumniProfile>> {
    return this.get<ApiResponse<AlumniProfile>>('/profile/v2');
  }

  // New types for history alumni response
  public async getSubmissionsAlumni(): Promise<MetaResponse<AlumniHistoryResponse>> {
    return this.get<MetaResponse<AlumniHistoryResponse>>('/alumni-profile/my-submissions');
  }

  public async searchAlumni(data: SearchAlumniRequest): Promise<MetaResponse<SearchAlumniResponse | null>> {
    return this.post<MetaResponse<SearchAlumniResponse | null>>('/alumni-profile/get-by-nib', data);
  }

  public async submitProfileAlumni(data: FormData | SubmitProfileAlumniRequest): Promise<any> {
    // Check if data is FormData (for file upload) or regular object
    if (data instanceof FormData) {
        const response = await this.client.post('/alumni-profile/submit', data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } else {
        return this.post<any>('/alumni-profile/submit', data);
    }
}

  public async updateProfile(data: Partial<Profile>): Promise<ApiResponse<Profile>> {
    return this.put<ApiResponse<Profile>>('/user/user_detail/update/v2', data);
  }

  public async updateFotoProfile(file: File): Promise<ApiResponse<{ avatar_url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await this.client.post('/user/user_detail/upload-image-tesis', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Registration methods
  public async registerThesis(data: any): Promise<MetaResponse<void>> {
    return this.post<MetaResponse<void>>('/auth/register-tesis', data);
  }

  // Statistics methods
  public async getTotalScolarship(): Promise<ApiResponse<{ total: number }>> {
    return this.get<ApiResponse<{ total: number }>>('/list/total/beasiswa');
  }

  // Privacy policy methods
  public async getPrivacyPolicy(data: { user_id: number }): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('/privacy-policy', data);
  }

  public async storePrivacyPolicy(data: { user_id: number; agreed: boolean }): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/privacy-policy/agree', data);
  }

  // Date and country methods
  public async getPublishDate(): Promise<ApiResponse<string[]>> {
    return this.get<ApiResponse<string[]>>('/list/tesis/date/publish');
  }

  public async getCountry(): Promise<ApiResponse<string[]>> {
    return this.get<ApiResponse<string[]>>('/list/tesis/country/publish');
  }

  // Template methods
  public async getTemplateMitra(type: string): Promise<ApiResponse<{ url: string }>> {
    return this.get<ApiResponse<{ url: string }>>(`/template-proposal/url-by-tipe/${type}`);
  }

  public async getTemplateFile(): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('/template-proposal/list-with-urls');
  }

  // Master table methods
  public async getMasterTable(params?: MasterTableQueryParams): Promise<PaginatedResponse<MasterTableItem[]>> {
    return this.get<PaginatedResponse<MasterTableItem[]>>('/list/master/table', params);
  }

  public async storeMasterTable(data: FormData): Promise<ApiResponse<any>> {
    const response = await this.client.post('/master/table', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  public async updateMasterTable(data: FormData, id: number): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/master/table/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Utility methods
  public async getTimeServer(): Promise<ApiResponse<{ server_time: string }>> {
    return this.get<ApiResponse<{ server_time: string }>>('/server-time');
  }

  // File upload utility
  public async uploadFile(file: File, endpoint: string): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

// Create and export a singleton instance
export const api = new LandingPageApi();

// Export types for external use
export type { 
  ApiResponse as ApiResponseType, 
  PaginatedResponse as PaginatedResponseType, 
  ApiError as ApiErrorType,
  User as UserType, 
  Thesis as ThesisType, 
  ScientificField as ScientificFieldType, 
  Language as LanguageType, 
  ThesisFeedback as ThesisFeedbackType, 
  Profile as ProfileType,
  MasterTableItem as MasterTableItemType,
  MasterTableQueryParams as MasterTableQueryParamsType,
  SubmitProfileAlumniRequest as SubmitProfileAlumniRequestType,
  AlumniProfile as AlumniProfileType,
  AlumniSubmission as AlumniSubmissionType,
  AlumniHistoryPagination as AlumniHistoryPaginationType,
  AlumniHistoryResponse as AlumniHistoryResponseType,
};